import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmEmailVerification, requestEmailVerification } from '@/utils/auth-pb';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { Loader2, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import LocalizedLink from '@/components/shared/LocalizedLink';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';

export default function EmailVerificationPage() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useLocalizedNavigate();
  const token = searchParams.get('token') || '';
  const emailFromUrl = searchParams.get('email') || '';

  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Автоматическая верификация при наличии токена
  useEffect(() => {
    if (token) {
      verifyEmail();
    }
  }, [token]);

  const verifyEmail = async () => {
    setIsVerifying(true);
    setError('');

    try {
      await confirmEmailVerification(token);
      setIsSuccess(true);
    } catch (err) {
      setError((err as { message: string }).message || t('emailVerification.errorDefault'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendEmail = async () => {
    if (!emailFromUrl) return;

    setError('');
    setResendSuccess(false);
    setIsLoading(true);

    try {
      await requestEmailVerification(emailFromUrl);
      setResendSuccess(true);
    } catch (err) {
      setError((err as { message: string }).message || t('emailVerification.resendError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Состояние загрузки при верификации
  if (isVerifying) {
    return (
      <AuthLayout title={t('emailVerification.title')}>
        <div className="flex flex-col items-center py-8">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#e8f4fd]">
            <Loader2 className="h-8 w-8 animate-spin text-[#2197f1]" />
          </div>
          <p className="text-[16px] text-[#565858]">{t('emailVerification.verifying')}</p>
        </div>
      </AuthLayout>
    );
  }

  // Успешная верификация
  if (isSuccess) {
    return (
      <AuthLayout title={t('emailVerification.successTitle')}>
        <div className="flex flex-col items-center py-8">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#e8f8e8]">
            <CheckCircle className="h-8 w-8 text-[#4caf50]" />
          </div>
          <p className="mb-8 text-center text-[16px] text-[#565858]">
            {t('emailVerification.successMessage')}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="h-[52px] w-full rounded-[12px] bg-[#2197f1] text-[16px] font-semibold text-white transition-colors hover:bg-[#1a7fd4]"
          >
            {t('emailVerification.goToApp')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Ошибка верификации по токену
  if (token && error) {
    return (
      <AuthLayout title={t('emailVerification.errorTitle')}>
        <div className="flex flex-col items-center py-8">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#fff0f0]">
            <AlertCircle className="h-8 w-8 text-[#ff5b63]" />
          </div>
          <p className="mb-2 text-center text-[16px] text-[#ff5b63]">{error}</p>
          <p className="mb-8 text-center text-[14px] text-[#565858]">
            {t('emailVerification.linkExpired')}
          </p>
          <LocalizedLink
            to="/login"
            className="flex h-[52px] w-full items-center justify-center rounded-[12px] bg-[#2197f1] text-[16px] font-semibold text-white transition-colors hover:bg-[#1a7fd4]"
          >
            {t('emailVerification.backToLogin')}
          </LocalizedLink>
        </div>
      </AuthLayout>
    );
  }

  // После регистрации - ожидание подтверждения
  return (
    <AuthLayout title={t('emailVerification.confirmEmailTitle')}>
      <div className="flex flex-col items-center py-4">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#e8f4fd]">
          <Mail className="h-8 w-8 text-[#2197f1]" />
        </div>

        <p className="mb-2 text-center text-[16px] text-[#565858]">
          {t('emailVerification.sentTo')}
        </p>
        <p className="mb-8 text-center text-[16px] font-semibold text-[#0d0d0d]">
          {emailFromUrl}
        </p>

        {error && (
          <div className="mb-4 w-full rounded-[12px] bg-[#fff0f0] p-4 text-center text-[14px] text-[#ff5b63]">
            {error}
          </div>
        )}

        {resendSuccess && (
          <div className="mb-4 w-full rounded-[12px] bg-[#e8f8e8] p-4 text-center text-[14px] text-[#4caf50]">
            {t('emailVerification.resendSuccess')}
          </div>
        )}

        <button
          onClick={handleResendEmail}
          disabled={isLoading || !emailFromUrl}
          className="mb-4 h-[52px] w-full rounded-[12px] bg-[#2197f1] text-[16px] font-semibold text-white transition-colors hover:bg-[#1a7fd4] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? t('emailVerification.resending') : t('emailVerification.resend')}
        </button>

        <LocalizedLink
          to="/register"
          className="text-[14px] text-[#2197f1] hover:underline"
        >
          {t('emailVerification.changeEmail')}
        </LocalizedLink>
      </div>
    </AuthLayout>
  );
}
